<template>
    <div
        class="control text-field"
        :class="{ 'text-field--with-suffix': suffix }"
        :style="controlStyle"
    >
        <label
            v-if="label"
            class="control__label control-label"
        >
            {{ label }}
            <span
                v-if="required"
                class="control__required"
            >*</span>
        </label>

        <v-text-field
            class="control__field"
            :autocomplete="autocomplete"
            :clearable="clearable"
            :disabled="disabled"
            :hide-details="hideDetails"
            :model-value="modelValue"
            :name="name || undefined"
            :placeholder="placeholder"
            :append-inner-icon="appendInnerIcon"
            :prepend-inner-icon="prependInnerIcon"
            :readonly="readonly"
            :rules="validationRules"
            :suffix="suffix"
            :type="type"
            :density="density"
            rounded="lg"
            variant="outlined"
            @blur="$emit('blur', $event)"
            @focus="$emit('focus', $event)"
            @update:model-value="$emit('update:modelValue', $event)"
        />
    </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },

  label: {
    type: String,
    default: '',
  },

  placeholder: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: '',
  },

  type: {
    type: String,
    default: 'text',
  },

  appendInnerIcon: {
    type: String,
    default: '',
  },

  prependInnerIcon: {
    type: String,
    default: '',
  },

  suffix: {
    type: String,
    default: '',
  },

  width: {
    type: [String, Number],
    default: '100%',
  },

  required: {
    type: Boolean,
    default: false,
  },

  rules: {
    type: Array,
    default: () => [],
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  readonly: {
    type: Boolean,
    default: false,
  },

  clearable: {
    type: Boolean,
    default: false,
  },

  autocomplete: {
    type: String,
    default: 'off',
  },

  hideDetails: {
    type: [Boolean, String],
    default: 'auto',
  },

  density: {
    type: String,
    default: 'compact',
    validator: (value) => ['default', 'comfortable', 'compact'].includes(value),
  },
})

defineEmits(['update:modelValue', 'blur', 'focus'])

const controlStyle = computed(() => {
  const width = typeof props.width === 'number' ? `${props.width}px` : props.width

  return {
    '--form-control-width': width,
  }
})

const validationRules = computed(() => {
  const rules = [...props.rules]

  if (props.required) {
    rules.unshift(
      (value) => String(value ?? '').trim().length > 0 || 'Obavezno polje'
    )
  }

  return rules
})
</script>

<style
    scoped
    lang="scss"
>
.text-field {
    --text-field-border-color: var(--color-control-border);
    --text-field-icon-color: var(--color-control-icon);

    :deep(.v-field) {
        background: var(--color-white);
    }

    :deep(.v-field__outline) {
        --v-field-border-width: 1px;
        --v-field-border-opacity: 1;
        color: var(--text-field-border-color);
    }

    :deep(.v-field__input) {
        padding: 0 16px;
        color: var(--color-text-body);
        font-size: 16px;
        font-weight: 300;
    }

    :deep(input.v-field__input::placeholder) {
        color: var(--color-text-placeholder);
        font-size: var(--placeholder-font-size);
        opacity: 1;
    }

    :deep(.v-field__append-inner),
    :deep(.v-field__prepend-inner) {
        align-items: center;
        padding-top: 0;
    }

    :deep(.v-field__append-inner) {
        padding-right: 16px;
    }

    :deep(.v-field__prepend-inner) {
        padding-left: 16px;
    }

    :deep(.v-icon) {
        color: var(--text-field-icon-color);
        opacity: 1;
    }

    &--with-suffix {
        :deep(.v-field) {
            overflow: hidden;
        }

        :deep(.v-field .v-text-field__suffix) {
            align-self: stretch;
            flex: 0 0 42px;
            justify-content: center;
            padding-block: 0;
            padding-inline: 0;
            background: #f2f2f2;
            color: #414145;
            opacity: 1;
        }
    }
}
</style>
